import { forwardRef, useId, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Checkbox } from './checkbox'
import { placed } from '@/lib/style-guard'
import { contentClasses, type ContentProps } from '@/lib/content-props'

/**
 * Row — the shared vocabulary for reading, disclosure and selection.
 *
 * DS2 spec: `design-system-v2/02-components/molecules/rows.html`.
 * Gate: `scripts/check-rows.mjs`.
 *
 * ## Why this exists
 *
 * Measured 07.08.2026: `components/ui/index.ts` exported 36 primitives and **no
 * row**. Three files were named `*Row*` (`ShareLinkRow`, `SettingToggleRow`,
 * `notification-row`) and were three unrelated implementations; every other
 * list in the app was hand-assembled markup — 200 × `flex items-center gap-2`,
 * 122 × `gap-1`, 45 × `gap-3`, 8 × `gap-4`.
 *
 * That single absence produced most of what the UI directive found:
 *
 *  - **No left rail.** Text started at a different x depending on whether the
 *    author put an icon, a chip or nothing in front of it, so nothing lined up
 *    down the page and the screen never settled.
 *  - **Affordance pointing the wrong way.** A chevron meant «navigate» in one
 *    place and «expand» in another; a non-tappable card looked identical to a
 *    tappable one.
 *  - **Cards used as decoration.** Five facts about one event became five
 *    bordered boxes, because a card was the only container anyone had.
 *
 * ## The row types
 *
 * | | Component | Job |
 * |---|---|---|
 * | A | `SectionRow`  | section header, the small sentence-case label (Parley, 24.10.2026) |
 * | B | `FieldRow`    | label + value, value right-aligned, tabular |
 * | C | `ItemRow`     | leading icon + title + optional subtitle + trailing affordance |
 * | D | `DisclosureRow` | the row that OPENS in place — leading chevron, rotates |
 * | E | `SelectionRow` | checkbox selection with identity, readable name, metadata and a separate action |
 *
 * ## The rail is a number, not a habit
 *
 * `ICON_SLOT` is a fixed 28px column. Text begins at the same x whether an icon
 * is present or not, by construction rather than by care — a row with no icon
 * still reserves the slot. This is the whole of directive rule 02, and it is
 * one constant.
 *
 * ## Affordance is binary, and the component decides
 *
 * Pass `to` or `onClick` and the row becomes a link/button, gets a trailing
 * chevron and a pressed state. Pass neither and it is a plain div with no
 * chevron and no hover. A caller cannot express «looks tappable, isn't» or
 * «is tappable, looks dead», which are the two states that made people guess.
 *
 * The chevron here is always TRAILING and always static. A leading chevron
 * that rotates is a DISCLOSURE — a different job, a different component, and
 * legitimately on the left. The rule the app follows is not «never on the
 * left», it is «one glyph, one meaning per position».
 *
 * ## D was the missing one, and its absence cost a surface
 *
 * That paragraph named the disclosure and then did not build it, so the next
 * list that had to open in place hand-rolled one. Measured 11.08.2026 on
 * `/kommunikasjon` → Rapporter, in the running app: three sends rendered as
 * three separate `Card`s, `space-y-3` between them (a third rhythm next to the
 * shell's 24 and the grid's 16), title starting at x=53 inside the card instead
 * of on the rail — and `CardContent`'s `pt-0` gave every one of them **0px of
 * padding above the text and 24px below it**. That asymmetry is what David saw
 * and asked about; it was never a token problem. `--card-padding` was 24px and
 * did exactly what it says. A card was simply the wrong container, chosen
 * because the right one did not exist.
 */

/** The rail. One number; everything lines up because of it. */
const ICON_SLOT = 'w-7 shrink-0' // 28px

/**
 * RowGroup — a shared panel edge and hairline dividers for a run of rows.
 *
 * This is what «group, don't stack» means in code: facts about the same object
 * go in ONE of these, not in N cards. Hairline only, no shadow (D-13).
 */
export function RowGroup({
  children,
  className,
  padded = false,
  ...rest
}: {
  children: ReactNode
  className?: string
  /** Inset rows that carry their own avatar or edge (people in a programme item). */
  padded?: boolean
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-[var(--card-radius)] border border-[var(--card-border)]',
        'bg-[var(--card-bg)]',
        '[&>*+*]:border-t [&>*+*]:border-[var(--border-default)]',
        padded && 'px-3',
        placed(className, 'RowGroup', rest),
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

/** Compact section identity with a separating rule, also used in pickers and dialogs. */
export function SectionRow({ children, count, className, id, compact = false }: {
  children: ReactNode
  count?: number
  className?: string
  id?: string
  /** A group label inside a picker list. */
  compact?: boolean
}) {
  return (
    <p id={id} className={cn('flex items-center gap-3 text-label font-semibold text-[var(--text-primary)]', compact ? 'px-2 pt-1 pb-1' : 'pb-3', placed(className, 'SectionRow'))}>
      <span className="min-w-0">{children}</span>
      {count !== undefined && <span className="shrink-0 rounded-[var(--radius-md)] bg-[var(--surface-muted)] px-2 text-small tabular-nums text-[var(--text-secondary)]">{' '}{count}</span>}
      <span aria-hidden="true" className="min-w-6 flex-1 border-t border-[var(--border-default)]" />
    </p>
  )
}

/** Supporting content stays on the identity rail; it is never a nested click target. */
export function RowDetails({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('space-y-2 pb-3 pl-14 pr-4', placed(className, 'RowDetails', props))} {...props}>{children}</div>
}

/** Quiet full-width band for supporting facts and independent record actions. */
export function RowFooter({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-default)] bg-[var(--surface-muted)] py-2 pl-14 pr-4', placed(className, 'RowFooter', props))} {...props}>{children}</div>
}

interface RowShellProps {
  icon?: LucideIcon
  className?: string
  children: ReactNode
}

/** The shared shell: rail, 44px floor, one padding value. */
function rowShell({ icon: Icon, className }: Omit<RowShellProps, 'children'>) {
  return {
    className: cn(
      /* THE ROW ITSELF DOES NOT WRAP. Only its text column does, and that wrap
         lives one level down (see `ItemRow`'s body).

         It used to be `flex-wrap` HERE, on the row, paired with a `basis` floor
         on the text column — and the pair answered «who gives way when the row
         runs out of width» by letting the LAST item fall to a second line. The
         last item is the chevron. Measured 11.08.2026 at 375px on
         `/event/:id?t=oversikt`, «Mine svar · 2 skjemaer besvart»: the row was
         73px instead of 44, the meta and the chevron sat on line two at x=33 —
         the ICON rail, left-aligned, under nothing — while the title's own rail
         is x=73. A trailing affordance that leaves the trailing edge is not an
         affordance any more, and David reported exactly that shape.

         It had been found once before and worked around at the call site:
         `help-result-row.tsx` dropped its trailing badge because «rowShell's
         flex-wrap put the badge at the end of line one and left the chevron
         alone on line two», concluding «the fix is upstream of it». This is
         upstream of it.

         What the wrap was FOR is preserved exactly, one level down: a long
         trailing text must not squeeze the text column to nothing. */
      'flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-left',
      className,
    ),
    lead: (
      <span className={cn(ICON_SLOT, 'flex items-center')} aria-hidden="true">
        {/* The icon is WAYFINDING, not content: one step below its own label,
            never the primary text colour and never the accent (D-5). An icon
            at the same contrast as its label makes the row read flat; an icon
            brighter than its label makes the icon the message. */}
        {Icon ? <Icon className="size-[18px] text-[var(--text-secondary)]" /> : null}
      </span>
    ),
  }
}

/**
 * B · FieldRow — label on the left, value on the right.
 *
 * The value is right-aligned with tabular figures and the unit one step
 * lighter, so a column of values reads as a table even when it is not one
 * (directive rule 11). Never tappable: a fact is not an action.
 */
export function FieldRow({
  icon,
  label,
  value,
  unit,
  className,
}: {
  icon?: LucideIcon
  label: ReactNode
  value: ReactNode
  /** Rendered one contrast step below the value — «kr», «plasser», «%». */
  unit?: ReactNode
  className?: string
}) {
  const shell = rowShell({ icon, className: placed(className, 'FieldRow') })
  return (
    <div className={shell.className}>
      {shell.lead}
      <span className="min-w-0 flex-1 text-body text-[var(--text-primary)]">{label}</span>
      {/* `shrink-0` because the shell no longer wraps: a long label now wraps
          its OWN text rather than pushing the value onto a second line, and
          `whitespace-nowrap` without it would let the value overflow instead. */}
      <span className="shrink-0 text-body font-semibold text-[var(--text-primary)] text-right tabular-nums whitespace-nowrap">
        {value}
        {unit != null && (
          <span className="ml-1 text-small font-normal text-[var(--text-muted)]">{unit}</span>
        )}
      </span>
    </div>
  )
}

/**
 * C · ItemRow — leading icon, title, optional subtitle, trailing affordance.
 *
 * Tappability is derived, never declared: `to` or `onClick` makes it a
 * link/button WITH a chevron and a pressed state; neither makes it a plain row
 * WITHOUT one. There is deliberately no `showChevron` prop — that would let a
 * caller re-create the exact ambiguity this replaces.
 */
export const ItemRow = forwardRef<
  HTMLElement,
  {
    icon?: LucideIcon
    title: ReactNode
    subtitle?: ReactNode
    /* Row states are props, not page classes (30.10.2026). */
    /** A notice row inside a list (an unmet requirement). */
    tone?: 'default' | 'warning'
    /** The row this picker is showing. */
    selected?: boolean
    /** No row padding: the row sits inside a container that already pads. */
    flush?: boolean
    /** Secondary status text (a save state). */
    quiet?: boolean
    /** Heavier ink for the whole row when it is a heading-like choice. */
    weight?: 'medium' | 'semibold'
    /** A standalone row with its own edge (an attached file, a template choice). */
    boxed?: boolean

    /** Right-hand slot for a badge or a count. Rendered before the chevron. */
    meta?: ReactNode
    /**
     * Trailing CONTROL — a delete, an overflow menu, a toggle.
     *
     * Separate from `meta` because they are different things: `meta` is a fact
     * about the row, this is something you can do to it. Added 10.08.2026 when
     * the saved-lists register needed a delete and the first draft hand-rolled
     * a `flex items-center gap-2` row to get one — which `check-rows` refused,
     * correctly: a bespoke row starts its text at a different x from every
     * other row in the app. The vocabulary is `OutcomeCard`'s, which has had
     * `meta` and `action` side by side for exactly this reason.
     *
     * The shell already supplies `gap-3`, so this needs no spacing of its own.
     * Do not combine with `to`/`onClick`: a control inside a tappable row is a
     * nested target, and the chevron would then be lying about what happens.
     */
    action?: ReactNode
    to?: string
    onClick?: () => void
    className?: string
    'aria-label'?: string
    /**
     * «This is the one you are on» — for a row that PICKS rather than navigates
     * away (`FormResponsePicker`). It replaces the `aria-selected` a tab strip
     * would have carried; a highlight colour alone says nothing to a screen
     * reader, and `aria-selected` is only legal inside a real tablist.
     */
    'aria-current'?: 'true' | undefined
  }
>(function ItemRow({ icon, title, subtitle, meta, action, to, onClick, className, tone = 'default', selected = false, flush = false, quiet = false, weight, boxed = false, ...rest }, ref) {
  const tappable = to != null || onClick != null
  const shell = rowShell({
    icon,
    className: cn(
      tone === 'warning' && 'rounded-[var(--radius-md)] border border-[var(--status-warning-border)] bg-[var(--status-warning-bg)]',
      selected && 'rounded-[var(--radius-sm)] bg-[var(--tint-selected)]',
      flush && 'px-0 py-0',
      quiet && 'text-small text-[var(--text-secondary)]',
      weight === 'medium' && 'font-medium',
      weight === 'semibold' && 'font-semibold',
      boxed && 'rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-elevated)]',
      tappable && [
        'cursor-pointer transition-colors duration-[var(--duration-fast)]',
        'hover:bg-[var(--surface-muted)] active:bg-[var(--surface-muted)]',
        'focus-ring',
      ],
      placed(className, 'ItemRow', rest),
    ),
  })

  const body = (
    <>
      {shell.lead}
      {/* THE WRAP LIVES HERE, and it moves the META and the ACTION.
          Title (+subtitle), meta and `action` share one wrapping column (the action
          joined it 30.10.2026, see below); only the chevron sits outside it, so the
          trailing affordance is the trailing edge at every width. When the meta does drop, it lands at the
          TITLE's rail — the same x as the subtitle — instead of at the icon
          rail, which is where the row-level wrap used to put it. */}
      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-0.5">
        {/* THE FLOOR IS DERIVED FROM THE CONTENT, not from a constant.
            `basis-48` (192px) was applied to every row, which is why a row as
            short as «Mine svar» wrapped: 192px of declared floor for 70px of
            title, so the meta could never fit beside it.

            What the floor is actually protecting is the SUBTITLE. Re-read the
            measurement it was written from — «the title column was 109px and
            its EXPLANATION set five lines in it»: a sentence needs width, a
            label does not. So a row with a subtitle keeps the 192px floor and
            behaves exactly as before (measured: the room row's column is 284px
            either way), and a row without one is free to shrink to its own
            content and let the meta sit beside it. */}
        <span className={cn('min-w-0', subtitle != null ? 'flex-1 basis-48' : 'flex-auto')}>
          {/* line-clamp-2, never truncate: a name is how people find things, and
              «Intim Tapas-kvel…» is not a name (directive rule 25). */}
          <span className="block text-body font-semibold text-[var(--text-primary)] leading-snug line-clamp-2">
            {title}
          </span>
          {subtitle != null && (
            <span className="mt-0.5 block text-small text-[var(--text-secondary)] leading-snug">
              {subtitle}
            </span>
          )}
        </span>
        {meta != null && (
          <span className="flex min-w-0 max-w-full flex-wrap items-center text-small text-[var(--text-secondary)]">{meta}</span>
        )}
        {/* The action is the LAST item of the wrapping column, pushed to its trailing edge.
            Wide, that is the same place as before. Narrow, it drops under the text instead
            of squeezing it: as a fixed slot outside the column, «Velg denne» + edit + delete
            left the date-poll text 64px at 375px, «septemb/er» (triple assessment 30.10.2026).
            The chevron stays outside, so the trailing affordance never moves. */}
        {action != null && <span className="ml-auto flex max-w-full items-center">{action}</span>}
      </span>
      {tappable && (
        <ChevronRight
          className="size-[18px] shrink-0 text-[var(--text-muted)]"
          aria-hidden="true"
        />
      )}
    </>
  )

  if (to != null) {
    return (
      <Link ref={ref as React.Ref<HTMLAnchorElement>} to={to} className={shell.className} {...rest}>
        {body}
      </Link>
    )
  }
  if (onClick != null) {
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={onClick}
        className={shell.className}
        {...rest}
      >
        {body}
      </button>
    )
  }
  return (
    <div ref={ref as React.Ref<HTMLDivElement>} className={shell.className} {...rest}>
      {body}
    </div>
  )
})

/**
 * D · DisclosureRow — the row that OPENS instead of navigating.
 *
 * Same shell as the other three, so it lands on the same rail with the same
 * 44px floor and the same padding; the only difference is what sits in the
 * leading slot and what the tap does.
 *
 * ## Why the chevron is on the LEFT here, and only here
 *
 * Position carries the meaning. Trailing chevron = «this takes you somewhere»,
 * and it never moves. Leading chevron = «this opens below», and it rotates 90°
 * when it does. A reader who has learned one has learned the other, which is
 * the entire argument for having both in one file rather than one per surface.
 *
 * ## What it owns that a hand-rolled version keeps forgetting
 *
 * `aria-expanded` AND `aria-controls` pointing at a panel that exists — the
 * id is generated here, so the two cannot drift apart. The panel is indented to
 * the rail (`pl-14` = 16 padding + 28 slot + 12 gap), so the detail lines up
 * under the title it belongs to instead of under the chevron.
 *
 * Unmounted when closed, not hidden: a closed disclosure holding a live list is
 * a list that still fetches, still renders and still reaches the accessibility
 * tree.
 */
export function DisclosureRow({
  title,
  subtitle,
  meta,
  open,
  onToggle,
  className,
  children,
}: {
  title: ReactNode
  subtitle?: ReactNode
  /** Right-hand slot: a badge, a count. Same job as `ItemRow`'s. */
  meta?: ReactNode
  open: boolean
  onToggle: () => void
  className?: string
  /** The panel. Rendered only while open. */
  children: ReactNode
}) {
  const panelId = useId()
  const shell = rowShell({
    className: cn(
      'cursor-pointer transition-colors duration-[var(--duration-fast)]',
      'hover:bg-[var(--surface-muted)] active:bg-[var(--surface-muted)] focus-ring',
      placed(className, 'DisclosureRow'),
    ),
  })
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className={shell.className}
      >
        <span className={cn(ICON_SLOT, 'flex items-center')} aria-hidden="true">
          <ChevronRight
            className={cn(
              'size-[18px] text-[var(--text-muted)]',
              'transition-transform duration-[var(--duration-fast)]',
              open && 'rotate-90',
            )}
          />
        </span>
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-0.5">
          <span className={cn('min-w-0', subtitle != null ? 'flex-1 basis-48' : 'flex-auto')}>
            <span className="block text-body font-semibold text-[var(--text-primary)] leading-snug line-clamp-2">
              {title}
            </span>
            {subtitle != null && (
              <span className="mt-0.5 block text-small text-[var(--text-secondary)] leading-snug">
                {subtitle}
              </span>
            )}
          </span>
          {meta != null && <span className="flex min-w-0 max-w-full flex-wrap items-center">{meta}</span>}
        </span>
      </button>
      {open && (
        <RowDetails id={panelId}>
          {children}
        </RowDetails>
      )}
    </div>
  )
}

/** Identity is readable text. Selection, navigation and editing stay separate. */
export function PersonIdentity({ title, subtitle }: { title: ReactNode; subtitle?: ReactNode }) {
  return <span className="block min-w-0">
    <span data-person-name className="block text-label font-medium [overflow-wrap:anywhere] text-[var(--text-primary)]">{title}</span>
    {subtitle && <span className="mt-1 block text-small [overflow-wrap:anywhere] text-[var(--text-secondary)]">{subtitle}</span>}
  </span>
}

/** Shared person structure. It responds to its container, including zoom,
 * side panels and mobile. No outer click target can swallow an inner action. */
export function PersonRow({ title, subtitle, identity, selection, meta, details, action }: {
  title: ReactNode
  subtitle?: ReactNode
  identity?: ReactNode
  selection?: { selected: boolean; onSelect: () => void; label: string; disabled?: boolean }
  meta?: ReactNode
  details?: ReactNode
  action?: ReactNode
}) {
  // Checkbox and avatar sit side by side on the NAME's line (03.10.2026 re-score): stacked, the
  // avatar sat under the checkbox and every row was ~110 px, so 2.5 people fitted above the fold.
  // Eventbrite Organizer's attendee rows are ~48 px for the same facts.
  return <div data-person-row className="@container/person py-2">
    <div className="flex items-start gap-2">
      {(selection || identity) && <span className="flex shrink-0 items-center gap-1">
        {selection && <label className="flex min-h-11 min-w-11 cursor-pointer items-center justify-center">
          <Checkbox checked={selection.selected} onCheckedChange={selection.onSelect} disabled={selection.disabled} aria-label={selection.label} />
        </label>}
        {identity && <span className="flex items-center" aria-hidden="true">{identity}</span>}
      </span>}
      <div className="grid min-w-0 flex-1 gap-3 @[36rem]/person:grid-cols-2 @[52rem]/person:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
        <div className="min-w-0">
          <PersonIdentity title={title} subtitle={subtitle} />
          {meta && <div className="mt-2 flex flex-wrap items-center gap-2 text-small text-[var(--text-secondary)]">{meta}</div>}
        </div>
        {details && <div className="min-w-0 space-y-2 text-small text-[var(--text-secondary)]">{details}</div>}
        {/* Actions side by side at the row's end, not stacked (03.10.2026): stacked, two actions
            set every row's height. */}
        {action && <div className="flex min-w-0 flex-wrap items-center gap-2 [&>button]:max-w-full [&>button]:whitespace-normal @[36rem]/person:col-span-2 @[52rem]/person:col-span-1 @[52rem]/person:justify-end">{action}</div>}
      </div>
    </div>
  </div>
}

/** A selection uses a checkbox, never a navigation chevron. Identity and role
 * slots share the same rails in a whole list; trailing actions stay separate. */
export function SelectionRow({
  title,
  subtitle,
  identity,
  meta,
  action,
  selected,
  onSelect,
  disabled,
  label,
}: {
  title: ReactNode
  subtitle?: ReactNode
  identity?: ReactNode
  meta?: ReactNode
  action?: ReactNode
  selected: boolean
  onSelect: () => void
  disabled?: boolean
  label: string
}) {
  return (
    <div
      className={cn(
        '@container/selection flex items-center gap-2 bg-[var(--surface-elevated)] px-2',
        selected && 'bg-[var(--surface-muted)]',
      )}
    >
      <label className="flex min-h-14 min-w-0 flex-1 cursor-pointer items-center gap-3 py-2">
        <Checkbox
          checked={selected}
          onCheckedChange={onSelect}
          disabled={disabled}
          aria-label={label}
        />
        {identity && <span className="w-8 shrink-0">{identity}</span>}
        <span className="flex min-w-0 flex-1 flex-col items-start gap-x-3 gap-y-1 @[22rem]/selection:flex-row @[22rem]/selection:items-center">
          <span className="min-w-0 w-full @[22rem]/selection:flex-1">
            <PersonIdentity title={title} subtitle={subtitle} />
          </span>
          {meta && (
            <span className="flex max-w-full flex-wrap items-center text-small text-[var(--text-secondary)]">{meta}</span>
          )}
        </span>
      </label>
      {action && <span className="flex shrink-0 items-center">{action}</span>}
    </div>
  )
}

/** A saved one-of-many setting. The whole labelled row is its hit target; unlike ItemRow it never implies navigation. */
export function RadioChoiceRow({
  name,
  value,
  checked,
  disabled,
  title,
  description,
  onSelect,
  describedBy,
}: {
  name: string
  value: string
  checked: boolean
  disabled?: boolean
  title: ReactNode
  description?: ReactNode
  onSelect: () => void
  describedBy?: string
}) {
  const shell = rowShell({
    className: cn(
      'cursor-pointer',
      checked && 'bg-[var(--surface-muted)]',
      disabled && 'cursor-not-allowed',
    ),
  })
  return (
    <label className={shell.className}>
      <span className={cn(ICON_SLOT, 'flex items-center')}>
        {/* ds-allow: rowShell supplies the labelled 44px minimum hit target; the native indicator is not the target boundary. */}
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={onSelect}
          className="size-4 shrink-0 accent-[var(--border-selected)] focus-ring"
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-label font-medium text-[var(--text-primary)]">{title}</span>
        {description && (
          <span className="mt-1 block text-small text-[var(--text-secondary)]">{description}</span>
        )}
      </span>
    </label>
  )
}

/* ── Parley rows (24.10.2026) ────────────────────────────────────────────────────────────── */

export type TileTone = 'brand' | 'violet' | 'teal' | 'ink' | 'quiet'

const TILE_TONE: Record<TileTone, string> = {
  brand: 'bg-[var(--surface-brand-subtle)] text-[var(--text-on-brand-subtle)]',
  violet: 'bg-[var(--tone-violet-bg)] text-[var(--tone-violet-fg)]',
  teal: 'bg-[var(--tone-teal-bg)] text-[var(--tone-teal-fg)]',
  ink: 'bg-[var(--surface-sunken)] text-[var(--text-secondary)]',
  quiet: 'bg-[var(--surface-sunken)] text-[var(--text-muted)]',
}

/** A coloured icon square: the visual cue that leads a Parley row or tile. */
export function IconTile({ icon: Icon, tone = 'violet', size = 'md', className }: { icon: LucideIcon; tone?: TileTone; size?: 'sm' | 'md'; className?: string }) {
  return (
    <span aria-hidden="true" className={cn('flex shrink-0 items-center justify-center rounded-[var(--radius-lg)]', size === 'sm' ? 'size-9' : 'size-10', TILE_TONE[tone], placed(className, 'IconTile'))}>
      <Icon className={size === 'sm' ? 'size-4' : 'size-5'} />
    </span>
  )
}

/**
 * TileRow: an icon tile, a title with one line under it, and one trailing thing (an action,
 * a chip, a chevron). The ONE row of the Parley cards: attention items, next steps, activity,
 * participation. `to` makes the whole row the target; `trailing` must then not be a link.
 */
export function TileRow({
  icon,
  tone = 'violet',
  title,
  subtitle,
  trailing,
  to,
  onClick,
  surface = 'muted',
  className,
}: {
  icon: LucideIcon
  tone?: TileTone
  title: ReactNode
  subtitle?: ReactNode
  trailing?: ReactNode
  to?: string
  onClick?: () => void
  /** `muted` = a filled row inside a card (Parley's attention list); `plain` = a list row. */
  surface?: 'muted' | 'plain'
  className?: string
}) {
  const body = (
    <>
      <IconTile icon={icon} tone={tone} size={surface === 'plain' ? 'sm' : 'md'} />
      <span className="min-w-0 flex-1">
        <span className="block text-label font-semibold text-[var(--text-primary)]">{title}</span>
        {subtitle && <span className="block truncate text-small text-[var(--text-muted)]">{subtitle}</span>}
      </span>
      {trailing}
      {to && !trailing && <ChevronRight className="size-5 shrink-0 text-[var(--text-muted)]" aria-hidden="true" />}
    </>
  )
  const shell = cn(
    'flex min-h-14 items-center gap-3 rounded-[var(--radius-xl)] focus-ring',
    surface === 'muted' ? 'bg-[var(--surface-muted)] p-3' : 'px-2 py-2',
    (to || onClick) && (surface === 'muted' ? 'transition-colors hover:bg-[var(--surface-sunken)]' : 'transition-colors hover:bg-[var(--surface-muted)]'),
    placed(className, 'TileRow'),
  )
  if (to) return <Link to={to} onClick={onClick} className={shell}>{body}</Link>
  if (onClick) return <button type="button" onClick={onClick} className={cn(shell, 'w-full text-left')}>{body}</button>
  return <div className={shell}>{body}</div>
}

/** An inline run (icon or swatch, then words): legends, chip contents, meta. */
export function Inline({ children, className, align, clamp, wrap, numeric, focus, gap = 'sm', meta = false }: ({ children: ReactNode; className?: string; gap?: 'xs' | 'sm' | 'md'; /** A figure-with-icon in a meta line. */ meta?: boolean }) & ContentProps) {
  return <span className={cn('content-fit inline-flex items-center', gap === 'xs' ? 'gap-1' : gap === 'sm' ? 'gap-1.5' : 'gap-3', meta && 'text-small font-semibold text-[var(--text-secondary)]', contentClasses({ align, clamp, wrap, numeric, focus }), placed(className, 'Inline'))}>{children}</span>
}

/** A card's top line: its heading, then one trailing thing (a link, a chip, a figure). */
export function CardHead({ children, trailing, className }: { children: ReactNode; trailing?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', placed(className, 'CardHead'))}>
      <div className="min-w-0 flex-1 basis-40">{children}</div>
      {trailing}
    </div>
  )
}

/** «Se svar →»: a text link with its arrow. `solid` is the filled variant on a hero card. */
export function ArrowLink({ to, children, tone = 'default', className }: { to: string; children: ReactNode; tone?: 'default' | 'onInk' | 'onHero' | 'solid'; className?: string }) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex min-h-11 items-center gap-1.5 self-start rounded-[var(--radius-lg)] text-label font-semibold focus-ring',
        tone === 'default' && 'hover:text-[var(--color-interactive-primary)]',
        tone === 'onInk' && 'text-[var(--ink-card-fg)] hover:opacity-80',
        tone === 'onHero' && 'text-[var(--hero-card-fg)] underline-offset-4 hover:underline',
        // The one action on a card: the action role, so it follows a tenant's brand too.
        tone === 'solid' && 'bg-[var(--btn-primary-bg)] px-3.5 text-[var(--btn-primary-text)]',
        placed(className, 'ArrowLink'),
      )}
    >
      {children}
      <ChevronRight className="size-4" aria-hidden="true" />
    </Link>
  )
}

/** A meter legend entry: the segment's look, then its words. */
export function Legend({ kind, tone = 'primary', children }: { kind: 'filled' | 'pending' | 'empty'; tone?: 'primary' | 'onHero'; children: ReactNode }) {
  // The meter's three states (done = positive green, not settled and declined = neutral), the same on every card.
  const swatch = kind === 'filled'
    ? 'bg-[var(--data-positive)]'
    : kind === 'pending'
      ? 'text-[var(--data-pending)] [background:var(--hatch)]'
      : tone === 'onHero' ? 'bg-[var(--hero-card-track)]' : 'bg-[var(--data-empty)]'
  return (
    <Inline>
      <span aria-hidden="true" className={cn('size-2.5 rounded-[3px]', swatch)} />
      {children}
    </Inline>
  )
}

/** A meter with its figure at the end: the people column of a list row or table. */
export function MeterRow({ meter, value, className }: { meter: ReactNode; value: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-center gap-3', placed(className, 'MeterRow'))}>
      <div className="min-w-0 flex-1">{meter}</div>
      {value}
    </div>
  )
}
